import type { PlansResponse } from "@ishvaram/api-client";

export type CheckoutPriceResolution =
  | { kind: "inr" }
  | { kind: "local"; checkoutAmountMinor: number; checkoutCurrency: string }
  | { kind: "missing" }
  | { kind: "invalid" };

export interface CheckoutPresentment {
  value: number;
  currency: string;
  amount_minor: number;
}

/** Resolve one report's buyer-specific display price from the server-authored
 * `/plans` response (ADR-0048). Report identity and INR catalog price stay
 * separate; these fields describe only what the provider will present. */
export function checkoutPriceForPlan(
  plans: PlansResponse,
  planId: string
): CheckoutPriceResolution {
  const report = (plans.premium_reports ?? []).find((candidate) => candidate.id === planId);
  if (!report) return { kind: "missing" };

  const amount = report.checkout_amount_minor;
  const currency = report.checkout_currency;
  if (amount == null && currency == null) return { kind: "inr" };
  if (
    !Number.isInteger(amount) ||
    typeof amount !== "number" ||
    amount <= 0 ||
    typeof currency !== "string" ||
    !/^[A-Z]{3}$/.test(currency)
  ) {
    return { kind: "invalid" };
  }
  return {
    kind: "local",
    checkoutAmountMinor: amount,
    checkoutCurrency: currency,
  };
}

/** Convert provider minor units using the ISO-4217 exponent owned by Intl. This
 * keeps zero- and three-decimal currencies correct when a future corridor is
 * added, without copying another currency table into application code. */
export function minorAmountToMajor(amountMinor: number, currency: string): number {
  const exponent = new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).resolvedOptions().maximumFractionDigits;
  if (exponent == null) {
    throw new Error(`Currency exponent is unavailable for ${currency}`);
  }
  return amountMinor / 10 ** exponent;
}

/** Analytics projection for the exact amount the buyer is shown and charged.
 * The pair is atomic: silently falling back to INR when only one local-price
 * field arrives would recreate the currency-misclassification incident. */
export function checkoutPresentment(opts: {
  priceInr: number;
  checkoutAmountMinor?: number | null;
  checkoutCurrency?: string | null;
}): CheckoutPresentment {
  const { priceInr, checkoutAmountMinor, checkoutCurrency } = opts;
  const hasAmount = checkoutAmountMinor != null;
  const hasCurrency = Boolean(checkoutCurrency);
  if (hasAmount !== hasCurrency) {
    throw new Error("Checkout presentment amount and currency must be supplied together");
  }
  if (hasAmount && checkoutCurrency) {
    if (!Number.isInteger(checkoutAmountMinor) || Number(checkoutAmountMinor) <= 0) {
      throw new Error("Checkout presentment amount must be a positive integer");
    }
    return {
      value: minorAmountToMajor(Number(checkoutAmountMinor), checkoutCurrency),
      currency: checkoutCurrency,
      amount_minor: Number(checkoutAmountMinor),
    };
  }
  return {
    value: priceInr,
    currency: "INR",
    amount_minor: Math.round(priceInr * 100),
  };
}

/** Format the exact card/modal price in the provider's presentment currency. */
export function formatCheckoutPrice(opts: {
  priceInr: number;
  checkoutAmountMinor?: number | null;
  checkoutCurrency?: string | null;
}): string {
  const { priceInr, checkoutAmountMinor, checkoutCurrency } = opts;
  const presentment = checkoutPresentment(opts);
  if (checkoutCurrency && checkoutAmountMinor != null) {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: presentment.currency,
      currencyDisplay: "narrowSymbol",
    }).format(presentment.value);
  }
  return `₹${priceInr.toLocaleString("en-IN")}`;
}
